import React from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  TablePagination,
} from "@mui/material";

export interface Column<T> {
  label: string;
  key: keyof T;
  format?: (value: any, row?: T) => React.ReactNode;
}

interface CustomTableProps<T extends { id: string }> {
  columns: Column<T>[];
  data: T[];
  loading?: boolean;
  rowActions?: (row: T) => React.ReactNode;
  // Basic pagination
  page: number;
  rowsPerPage: number;
  onPageChange: (newPage: number) => void;
  onRowsPerPageChange: (newRowsPerPage: number) => void;
  rowsPerPageOptions?: number[];
}

function CustomTable<T extends { id: string }>({
  columns,
  data,
  loading = false,
  rowActions,
  page,
  rowsPerPage,
  onPageChange,
  onRowsPerPageChange,
  rowsPerPageOptions = [10, 25, 50, 100],
}: CustomTableProps<T>) {
  const handleChangePage = (_e: unknown, newPage: number) => {
    onPageChange(newPage);
  };

  const handleChangeRowsPerPage = (e: React.ChangeEvent<HTMLInputElement>) => {
    onRowsPerPageChange(parseInt(e.target.value, 10));
  };

  // Ensure data is an array and is not null/undefined
  const safeData = Array.isArray(data) ? data : [];

  return (
    <Paper>
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              {columns.map((col) => (
                <TableCell key={`header-${String(col.key)}`}>
                  {col.label}
                </TableCell>
              ))}
              {rowActions && <TableCell key="header-actions">Actions</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow key="loading">
                <TableCell
                  colSpan={columns.length + (rowActions ? 1 : 0)}
                  align="center"
                >
                  <CircularProgress size={24} />
                </TableCell>
              </TableRow>
            ) : safeData.length > 0 ? (
              safeData.map((row) => {
                // Skip rendering if row doesn't have an id
                if (!row || !row.id) {
                  console.warn('Row is missing an id property:', row);
                  return null;
                }
                
                return (
                  <TableRow key={row.id}>
                    {columns.map((col) => {
                      const rawValue = row[col.key];
                      return (
                        <TableCell key={`cell-${row.id}-${String(col.key)}`}>
                          {col.format
                            ? col.format(rawValue, row) // Pass the entire row to the format function
                            : (rawValue !== null && rawValue !== undefined && (
                                typeof rawValue === "string" ||
                                typeof rawValue === "number" ||
                                typeof rawValue === "boolean"
                              ))
                              ? String(rawValue)
                              : "-"}
                        </TableCell>
                      );
                    })}
                    {rowActions && (
                      <TableCell key={`cell-${row.id}-actions`}>
                        {rowActions(row)}
                      </TableCell>
                    )}
                  </TableRow>
                );
              })
            ) : (
              <TableRow key="no-data">
                <TableCell
                  colSpan={columns.length + (rowActions ? 1 : 0)}
                  align="center"
                >
                  No data available.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        count={safeData.length}
        page={page}
        onPageChange={handleChangePage}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        rowsPerPageOptions={rowsPerPageOptions}
      />
    </Paper>
  );
}

export default CustomTable;
